import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';

import React, { useContext, useMemo, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import {
  NotificationService,
  NOTIFICATION_DURATIONS,
  NOTIFICATION_TYPE_SUCCESS,
  NOTIFICATION_TYPE_ERROR,
} from 'ui-components/services/notification-service';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { Text, TextType } from 'ui-components/components/text';
import { Button } from 'ui-components/components/button';

import { getSeller } from '../../../store/seller/seller.actions';
import { ChildrenPropType } from '../../../prop-types/children';
import { UpsellModal } from '../../../components/upsell-modal/upsell-modal';
import { ProviderTypes } from '../../../connection-components/constants';
import { SetupCubiscanModal } from '../../../components/modals/setup-cubiscan-modal';
import { useQuery } from '../../../hooks/routing/use-query';
import { useTrackMarketplaceConnection, stepNames } from '../stores/tracking-events';
import { useTrackWorkflow, WorkflowSteps } from '../../../tracking/use-workflow-tracking';
import { Workflows } from '../../../tracking/tracking-constants';
import { SetupProductAutoSplittingModal } from '../../../components/modals/setup-product-auto-splitting-modal';
import { useBuildMarketplaceAddOns } from '../../../hooks/add-ons/use-build-addons';
import { AddOnDeactivationModal } from '../components/add-on-deactivation-modal';
import MarketplaceConnectionModal from '../stores/marketplace-connection-modal/settings';
import {
  AddOnCodeQueryParam,
  ActivateAddOnModalQueryParam,
  DeactivateAddOnModalQueryParam,
  ADD_WORKFLOW_TYPE_ACTIVATION,
  ADD_WORKFLOW_TYPE_DEACTIVATION,
} from './constants';
import { pages } from '../nav-items';

import { AddOns } from '../../../constants/add-ons';
import { ShipStationApiV2 } from '../../../constants/features';
import { ReturnRabbit } from '../../../constants/upsell-modal-content-codes';

import {
  enableAddOn as enableAddOnApi,
  configureReturnRabbitAddOn as configureReturnRabbitAddOnApi,
  getReturnRabbitAdminPortalUrl,
} from '../api/add-ons-api';
import { getUpsellItemName } from '../../../components/upsell-modal/helper';
import Global from '../../../store/global-selectors';

import styles from './style.module.less';
import { messages as upsellMessages } from '../../../components/upsell-modal/confirm-upsell-button/messages';

export const AddOnsActionContext = React.createContext();
export const useAddOnsActionContext = () => useContext(AddOnsActionContext);
export const redirectToReturnRabbit = () => {
  const url = getReturnRabbitAdminPortalUrl();
  window.open(url, '_blank');
};

export const AddOnsActionContextProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [marketPlaceModalCode, setMarketPlaceModalCode] = useState(null);
  const [openCubiscan, setOpenCubiscan] = useState(false);
  const [openAutoSplit, setOpenAutoSplit] = useState(false);
  // NOTE: the auth0 login/redirect mechanism is toLowering the entire url after login redirect.
  // i.e. if you go to ship0.shipstation.com?ActivateAddOnModal=true but not authed, you will be redirected to the auth0 login page.
  // after auth, it redirects to ship0.shipstation.com?activateaddonmodal=true which breaks our query param check.
  // so we to lower it all for comparison
  const shouldLowerCaseQueryParams = true;
  const query = useQuery(shouldLowerCaseQueryParams);
  const [addOnCodeWithError, setAddOnCodeWithError] = useState(null);
  const trackMarketplaceConnection = useTrackMarketplaceConnection();
  const trackAddOnWorkflow = useTrackWorkflow(Workflows.ADD_ON_FLOW);
  const isActivateModalOpen = Boolean(query.get(ActivateAddOnModalQueryParam.toLowerCase()));
  const isDeactivateModalOpen = Boolean(query.get(DeactivateAddOnModalQueryParam.toLowerCase()));
  const selectedAddOnCode = query.get(AddOnCodeQueryParam.toLowerCase());
  const history = useHistory();

  const stores = useSelector(Global.selectSellerActiveStores);
  const { enabledAddons, disabledAddons } = useBuildMarketplaceAddOns();

  const getAddOn = useCallback(
    (code) => {
      const addOns = [...enabledAddons, ...disabledAddons];
      return addOns.find((r) => r.code === code);
    },
    [enabledAddons, disabledAddons],
  );

  const onClickActivateAddOn = useCallback(
    (code) => {
      const addOn = getAddOn(code);
      const shouldShowReactivateAction = !!addOn?.deactivationDateTimeUTC;
      trackAddOnWorkflow(WorkflowSteps.START, {
        workFlowType: ADD_WORKFLOW_TYPE_ACTIVATION,
        addOnCode: code,
        isAvailableToTrial: addOn.canTrial && !addOn.isEnabled,
        isReactivation: shouldShowReactivateAction,
      });
      setAddOnCodeWithError(null);
      history.push({
        search: `?${ActivateAddOnModalQueryParam}=true&${AddOnCodeQueryParam}=${code}`,
      });
    },
    [history, setAddOnCodeWithError, getAddOn, trackAddOnWorkflow],
  );

  const onActivateDeactivateModalClose = useCallback(() => {
    history.push(history.location.pathname);
    trackAddOnWorkflow(WorkflowSteps.CANCEL);
  }, [history, trackAddOnWorkflow]);

  const onClickDeactivateAddon = useCallback(
    (code) => {
      const addOn = getAddOn(code);
      trackAddOnWorkflow(WorkflowSteps.START, {
        workFlowType: ADD_WORKFLOW_TYPE_DEACTIVATION,
        addOnCode: code,
        isTrial: addOn.isTrial,
      });
      setAddOnCodeWithError(null);
      history.push({
        search: `?${DeactivateAddOnModalQueryParam}=true&${AddOnCodeQueryParam}=${code}`,
      });
    },
    [history, setAddOnCodeWithError, getAddOn, trackAddOnWorkflow],
  );

  const resetAddOnSettingsModalState = useCallback(() => {
    setMarketPlaceModalCode(null);
    setOpenCubiscan(false);
    setOpenAutoSplit(false);
  }, [setMarketPlaceModalCode, setOpenCubiscan]);

  const onODBCMarketPlaceModalClose = useCallback(() => {
    trackMarketplaceConnection(WorkflowSteps.CANCEL, {
      stepName: stepNames.STORE_SPECIFIC_SETUP_MODAL,
    });
    resetAddOnSettingsModalState();
  }, [trackMarketplaceConnection, resetAddOnSettingsModalState]);

  const onODBCMarketPlaceSave = useCallback(() => {
    trackMarketplaceConnection(WorkflowSteps.COMPLETE);
    resetAddOnSettingsModalState();
  }, [trackMarketplaceConnection, resetAddOnSettingsModalState]);

  const configureReturnRabbit = useCallback(async () => {
    try {
      await configureReturnRabbitAddOnApi();
      return redirectToReturnRabbit();
    } catch {
      redirectToReturnRabbit();
    }
  }, []);

  const enableReturnRabbit = useCallback(async () => {
    const nameDescriptor = getUpsellItemName(ReturnRabbit);
    const addOnName = <FormattedMessage {...nameDescriptor} />;
    const nonManualStores = stores.filter((store) => !store.isManualStore);

    if (!nonManualStores.length) {
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        title: <FormattedMessage values={{ addOnName }} {...upsellMessages.activateAddOnFailureNotificationTitle} />,
        content: (
          <>
            <Text textType={TextType.Paragraph} value={upsellMessages.returnRabbitFailureNoStoresNotification} />
            <Button href='/settings/stores' target='_self' theme='link'>
              <Text textType={TextType.Paragraph} value={upsellMessages.storeSetup} />
            </Button>
          </>
        ),
        duration: NOTIFICATION_DURATIONS.SHORT,
      });
      return;
    }

    await configureReturnRabbitAddOnApi();

    NotificationService.showNotification({
      notificationType: NOTIFICATION_TYPE_SUCCESS,
      title: <FormattedMessage values={{ addOnName }} {...upsellMessages.activateAddOnSuccessNotificationTitle} />,
      content: <FormattedMessage values={{ addOnName }} {...upsellMessages.returnRabbitAddOnSuccessNotificationTitle} />,
      buttons: [
        {
          text: <FormattedMessage {...upsellMessages.configureAccount} />,
          theme: THEME_CONSTANTS.link,
          callback: redirectToReturnRabbit,
        },
      ],
      duration: NOTIFICATION_DURATIONS.LONG,
    });

    redirectToReturnRabbit();
  }, [stores]);

  const enableShipStationApiV2 = useCallback(
    async (code) => {
      await enableAddOnApi(code);

      const nameDescriptor = getUpsellItemName(code);
      const addOnName = <FormattedMessage {...nameDescriptor} />;

      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_SUCCESS,
        title: <FormattedMessage values={{ addOnName }} {...upsellMessages.activateAddOnSuccessNotificationTitle} />,
        content: (
          <>
            <Text
              className={styles['shipstation-notification-text']}
              textType={TextType.Paragraph}
              value={upsellMessages.shipstationV2AddOnSuccessNotificationText}
              values={{ addOnName }}
            />
            <Button onClick={() => history.push(`./${pages.api.location}`)} target='_self' theme='link'>
              <Text className={styles['go-to-api-keys-link']} textType={TextType.Paragraph} value={upsellMessages.apiSettings} />
              <FontAwesomeIcon className={styles['right-arrow-icon']} icon={faArrowRight} />
            </Button>
          </>
        ),
        duration: NOTIFICATION_DURATIONS.LONG,
      });
    },
    [history],
  );

  const enableAddOn = useCallback(async (code) => {
    await enableAddOnApi(code);

    const nameDescriptor = getUpsellItemName(code);
    const addOnName = <FormattedMessage {...nameDescriptor} />;

    NotificationService.showNotification({
      notificationType: NOTIFICATION_TYPE_SUCCESS,
      title: <FormattedMessage values={{ addOnName }} {...upsellMessages.activateAddOnSuccessNotificationTitle} />,
      content: <FormattedMessage values={{ addOnName }} {...upsellMessages.activateAddOnSuccessNotificationText} />,
      duration: NOTIFICATION_DURATIONS.LONG,
    });
  }, []);

  // NOTE: intentionally no try/catch as it's currently handled in the ConfirmAddOnUpsellButton
  const onEnableAddOn = useCallback(
    async (code) => {
      switch (code) {
        case ReturnRabbit:
          await enableReturnRabbit();
          break;
        case ShipStationApiV2:
          await enableShipStationApiV2(code);
          break;
        default:
          await enableAddOn(code);
          break;
      }

      trackAddOnWorkflow(WorkflowSteps.COMPLETE);
      dispatch(getSeller());
    },
    [dispatch, enableAddOn, enableReturnRabbit, enableShipStationApiV2, trackAddOnWorkflow],
  );

  const onClickManageSettings = useCallback(
    async (code, source) => {
      switch (code) {
        case AddOns.Cubiscan:
          setOpenCubiscan(true);
          break;
        case AddOns.ODBC:
          trackMarketplaceConnection(WorkflowSteps.START, {
            selectedMarketplace: { marketplaceId: 102, name: AddOns.ODBC },
            marketplaceName: AddOns.ODBC,
            marketplaceId: 102,
            source,
          });
          setMarketPlaceModalCode(ProviderTypes.odbc);
          break;
        case AddOns.ShippingStrategies:
          history.push(`./${pages.serviceCollections.location}`);
          break;
        case AddOns.AutoSplit:
          setOpenAutoSplit(true);
          break;
        case AddOns.InventorySync:
          history.push(`./${pages.inventorySync.location}`);
          break;
        case ReturnRabbit:
          await configureReturnRabbit();
          break;
        case AddOns.InventoryApi:
          history.push(`./${pages.api.location}`);
          break;
        case AddOns.ShipStationApiV2:
          history.push(`./${pages.api.location}`);
          break;
        default:
          break;
      }
    },
    [trackMarketplaceConnection, history, configureReturnRabbit],
  );

  const selectedAddOn = isDeactivateModalOpen
    ? enabledAddons.find((r) => r?.code?.toLowerCase() === selectedAddOnCode?.toLowerCase())
    : disabledAddons.find((r) => r?.code?.toLowerCase() === selectedAddOnCode?.toLowerCase());

  const value = useMemo(
    () => ({
      enabledAddons,
      disabledAddons,
      addOnCodeWithError,
      setAddOnCodeWithError,
      onClickActivateAddOn,
      onClickDeactivateAddon,
      onClickManageSettings,
      onEnableAddOn,
    }),
    [enabledAddons, disabledAddons, addOnCodeWithError, onClickActivateAddOn, onClickDeactivateAddon, onClickManageSettings, onEnableAddOn],
  );

  return (
    <AddOnsActionContext.Provider value={value}>
      {children}
      {isActivateModalOpen && selectedAddOn && (
        <UpsellModal onClose={onActivateDeactivateModalClose} onError={setAddOnCodeWithError} upsellModalContentCode={selectedAddOn.code} />
      )}
      {isDeactivateModalOpen && selectedAddOn && (
        <AddOnDeactivationModal addOn={selectedAddOn} onClose={onActivateDeactivateModalClose} onError={setAddOnCodeWithError} />
      )}
      {marketPlaceModalCode && (
        <MarketplaceConnectionModal
          marketplaceApiCode={marketPlaceModalCode}
          onClose={onODBCMarketPlaceModalClose}
          onSave={onODBCMarketPlaceSave}
        />
      )}
      {openCubiscan && <SetupCubiscanModal onClose={resetAddOnSettingsModalState} />}
      {openAutoSplit && <SetupProductAutoSplittingModal onClose={resetAddOnSettingsModalState} />}
    </AddOnsActionContext.Provider>
  );
};

AddOnsActionContextProvider.propTypes = {
  children: ChildrenPropType.isRequired,
};
